import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Text,
} from '@bloobirds-it/flamingo-ui';
import { BobjectApi } from '../../misc/api/bobject';
import { useAddToCalendar, useEntity } from '../../hooks';
import { useUserSettings } from '../userPermissions/hooks';
import { getValueFromLogicRole } from '../../utils/bobjects.utils';
import { addMinutes, formatDate } from '../../utils/dates.utils';
import { buildCalendarLink } from './addToCalendarModal.utils';
import { methods } from './addToCalendarModal.constants';
import styles from './addToCalendarModal.module.css';
import {
  COMPANY_FIELDS_LOGIC_ROLE,
  LEAD_FIELDS_LOGIC_ROLE,
  OPPORTUNITY_FIELDS_LOGIC_ROLE,
} from '@bloobirds-it/types';

const getBobjectData = (entity, id) => BobjectApi.request().bobjectType(entity).getForm(id);

const AddToCalendarModal = () => {
  const { closeAddToCalendarModal, addToCalendarState } = useAddToCalendar();
  const {
    leadId,
    companyId,
    opportunityId,
    title,
    dateTime,
    accountExecutiveId,
    bobjectType,
  } = addToCalendarState;
  const settings = useUserSettings();
  const isOutlook = settings?.settings.calendarLinksType !== 'GOOGLE_CALENDAR';
  const [lead, setLead] = useState();
  const [company, setCompany] = useState();
  const [opportunity, setOpportunity] = useState();
  const bobjectPicklistFieldValues = useEntity('bobjectPicklistFieldValues');
  const eventTitle = title || 'Untitled event';

  useEffect(() => {
    if (leadId && (!lead || lead.id.value !== leadId)) {
      getBobjectData('Lead', leadId.split('/')[2]).then(response => {
        setLead(response);
      });
    }
  }, [leadId, lead]);

  useEffect(() => {
    if (companyId && (!company || company?.id.value !== companyId)) {
      getBobjectData('Company', companyId.split('/')[2]).then(response => {
        setCompany(response);
      });
    }
  }, [companyId, company]);

  useEffect(() => {
    if (opportunityId && (!opportunity || opportunity?.id.value !== opportunityId)) {
      getBobjectData('Opportunity', opportunityId.split('/')[2]).then(response => {
        setOpportunity(response);
      });
    }
  }, [opportunityId, opportunity]);

  const name = useMemo(() => {
    if (opportunity) {
      return getValueFromLogicRole(opportunity, OPPORTUNITY_FIELDS_LOGIC_ROLE.NAME);
    }
    if (lead) {
      return getValueFromLogicRole(lead, LEAD_FIELDS_LOGIC_ROLE.FULL_NAME);
    }
    if (company) {
      return getValueFromLogicRole(company, COMPANY_FIELDS_LOGIC_ROLE.NAME);
    }
    return '';
  }, [company, lead, opportunity]);

  const url = useMemo(() => {
    const eventLeadName = name && ` with ${name}`;
    const calendarEventTitle = `${eventTitle}${eventLeadName}`;
    const leadEmail = getValueFromLogicRole(lead, 'LEAD__EMAIL');
    const guests = [];

    if (leadEmail && bobjectType === 'Activity') {
      guests.push(leadEmail);
    }
    if (accountExecutiveId && bobjectType === 'Activity') {
      guests.push(bobjectPicklistFieldValues.findBy('id')(accountExecutiveId).value);
    }

    return (
      dateTime &&
      buildCalendarLink(
        {
          title: calendarEventTitle,
          fromDate: dateTime,
          toDate: addMinutes(dateTime, 30),
          guests,
        },
        isOutlook ? methods.METHOD_OUTLOOK : methods.METHOD_GOOGLE,
      )
    );
  }, [name, title, dateTime, settings, lead]);

  const openLink = () => {
    window.open(url, '_blank');
    closeAddToCalendarModal();
  };

  return (
    <Modal open onClose={closeAddToCalendarModal} width={500}>
      <ModalHeader>
        <ModalTitle>Add to your calendar?</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <Text color="softPeanut" size="m" align="center">
          Book a 30-minute meeting in your calendar
        </Text>
        <div className={styles._main}>
          <div className={styles._calendar}>
            <header>
              <Text align="center" size="m" weight="bold" color="white">
                {formatDate(dateTime, 'MMM').toUpperCase()}
              </Text>
            </header>
            <div>
              <Text align="center" size="xxl" weight="bold" color="peanut">
                {dateTime?.getDate()}
              </Text>
              <Text align="center" size="s" color="peanut">
                {formatDate(dateTime, 'hh:mm a').toLowerCase()}
              </Text>
            </div>
          </div>
          <div className={styles._text}>
            <Text size="l" weight="bold" color="peanut">
              {eventTitle}
            </Text>
            {name && (
              <Text size="m" color="peanut">
                with {name}
              </Text>
            )}
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button dataTest="Form-Close" onClick={closeAddToCalendarModal} variant="tertiary">
          Close
        </Button>
        <Button onClick={openLink} variant="primary">
          Add to calendar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddToCalendarModal;
